/**
 * Unified hook for monitoring generation status.
 *
 * Uses WebSocket as the primary transport for real-time updates.
 * Falls back to HTTP polling (via react-query) if the WebSocket
 * disconnects before the job reaches a terminal state.
 */

import { useCallback, useEffect, useRef, useState } from "react"

import { useQuery } from "@tanstack/react-query"

import type { VideoGenerationResult } from "@/features/studio-ai/types/studio-ai.types"

import { connectGenerationWebSocket, getVideoStatus } from "@/api/studio-ai-api"
import { queryKeys } from "@/lib/query-keys"
import { useStudioAIStore } from "@/stores/studio-ai-store"

interface UseGenerationMonitorOptions {
  jobId: string | null
  enabled?: boolean
  mediaType?: "video" | "image"
  onComplete?: (result: VideoGenerationResult) => void
  onError?: (error: string) => void
  onPartialImage?: (imageUrl: string, progress: number) => void
}

export function useGenerationMonitor({
  jobId,
  enabled = true,
  mediaType = "video",
  onComplete,
  onError,
  onPartialImage,
}: UseGenerationMonitorOptions) {
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState("")

  const updateGenerationState = useStudioAIStore((state) => state.updateGenerationState)
  const updateVideo = useStudioAIStore((state) => state.updateVideo)

  // ----- Refs to prevent duplicate connections / callbacks -----
  const activeJobIdRef = useRef<string | null>(null)
  const wsRef = useRef<{ close: () => void } | null>(null)

  /** True once WebSocket completes or errors (terminal). */
  const wsCompletedRef = useRef(false)

  /** True when we should enable HTTP polling (WS failed before completion). */
  const [pollingEnabled, setPollingEnabled] = useState(false)

  /** Prevents duplicate completion callbacks from the polling path. */
  const hasPollingFiredRef = useRef(false)

  // Stabilize store methods with refs
  const updateGenerationStateRef = useRef(updateGenerationState)
  const updateVideoRef = useRef(updateVideo)

  // Stabilize external callbacks with refs to prevent reconnections
  const onCompleteRef = useRef(onComplete)
  const onErrorRef = useRef(onError)
  const onPartialImageRef = useRef(onPartialImage)

  useEffect(() => {
    updateGenerationStateRef.current = updateGenerationState
    updateVideoRef.current = updateVideo
    onCompleteRef.current = onComplete
    onErrorRef.current = onError
    onPartialImageRef.current = onPartialImage
  }, [updateGenerationState, updateVideo, onComplete, onError, onPartialImage])

  // ----- WebSocket callbacks (stable) -----

  const handleProgress = useCallback(
    (prog: number, stat: string, data?: unknown) => {
      setProgress(prog)
      setStatus(stat)
      updateGenerationStateRef.current({
        progress: prog,
        status: stat,
      })

      if (data && jobId) {
        const videoData = data as Partial<VideoGenerationResult>
        updateVideoRef.current(jobId, {
          status: videoData.status,
          video_url: videoData.video_url,
          thumbnail_url: videoData.thumbnail_url,
        })
      }
    },
    [jobId]
  )

  const handleComplete = useCallback(
    (result: VideoGenerationResult) => {
      wsCompletedRef.current = true
      setProgress(100)
      setStatus("completed")
      updateGenerationStateRef.current({
        isGenerating: false,
        progress: 100,
        status: "completed",
      })

      if (jobId) {
        updateVideoRef.current(jobId, {
          status: result.status,
          video_url: result.video_url,
          thumbnail_url: result.thumbnail_url,
          completed_at: result.completed_at,
        })
      }

      if (onCompleteRef.current) {
        onCompleteRef.current(result)
      }
    },
    [jobId]
  )

  const handleError = useCallback((error: string) => {
    wsCompletedRef.current = true
    setStatus("failed")
    updateGenerationStateRef.current({
      isGenerating: false,
      status: "failed",
    })

    if (onErrorRef.current) {
      onErrorRef.current(error)
    }
  }, [])

  // ----- WebSocket effect (primary transport) -----

  useEffect(() => {
    // Image jobs are streamed end-to-end by their own generation WebSocket
    // (use-generate-image) — they have no entry in the video-status path, so
    // polling /api/ws/studio-ai/status for them only produces backend errors
    // (get_job returns None → "Job lookup failed"). Never open the status WS
    // or the HTTP polling fallback for images.
    if (!jobId || !enabled || mediaType === "image") {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
        activeJobIdRef.current = null
      }
      return
    }

    // Already connected to this exact job
    if (activeJobIdRef.current === jobId && wsRef.current) {
      return
    }

    // Close any existing connection to a different job
    if (wsRef.current && activeJobIdRef.current !== jobId) {
      wsRef.current.close()
      wsRef.current = null
    }

    // Reset state for the new job
    activeJobIdRef.current = jobId
    wsCompletedRef.current = false
    setPollingEnabled(false)
    hasPollingFiredRef.current = false

    const ws = connectGenerationWebSocket({
      jobId,
      mediaType,
      onProgress: handleProgress,
      onComplete: handleComplete,
      onError: handleError,
      onPartialImage: onPartialImageRef.current,
    })

    // Intercept the native onclose to detect early disconnects
    const originalOnClose = ws.onclose
    ws.onclose = (event: CloseEvent) => {
      if (originalOnClose) {
        originalOnClose.call(ws, event)
      }

      // If the WS closed before the job finished, enable HTTP polling fallback
      if (!wsCompletedRef.current) {
        setPollingEnabled(true)
      }
    }

    wsRef.current = ws

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
      activeJobIdRef.current = null
    }
  }, [jobId, enabled, handleProgress, handleComplete, handleError, mediaType])

  // ----- HTTP polling fallback (video only) -----
  // Image jobs stream to completion over their own generation WebSocket and
  // have no entry in the video-status path, so polling is gated to videos via
  // `mediaType !== "image"` below. The WS effect above applies the same gate.

  const query = useQuery({
    queryKey: queryKeys.sora2.videoStatus(jobId || ""),
    queryFn: () => getVideoStatus(jobId!),
    enabled: pollingEnabled && enabled && !!jobId && mediaType !== "image",
    refetchInterval: (query) => {
      if (!query.state.data) return false

      const dataStatus = String(query.state.data.status)
      const terminalStates = ["completed", "succeeded", "failed", "cancelled"]

      if (terminalStates.includes(dataStatus)) {
        // Fire callbacks exactly once via the polling path
        if (!hasPollingFiredRef.current) {
          hasPollingFiredRef.current = true

          const isCompleted = dataStatus === "completed" || dataStatus === "succeeded"
          if (isCompleted && onCompleteRef.current) {
            onCompleteRef.current(query.state.data)
          } else if (dataStatus === "failed" && onErrorRef.current) {
            onErrorRef.current(query.state.data.error_message || "Generation failed")
          }
        }

        return false // stop polling
      }

      return 15000
    },
    refetchOnWindowFocus: false,
  })

  // Sync polling data to store
  useEffect(() => {
    if (query.data && jobId && pollingEnabled) {
      updateVideo(jobId, {
        status: query.data.status,
        video_url: query.data.video_url,
        thumbnail_url: query.data.thumbnail_url,
        error_message: query.data.error_message,
        completed_at: query.data.completed_at,
      })
    }
  }, [query.data, jobId, updateVideo, pollingEnabled])

  // Reset polling flag when jobId changes
  useEffect(() => {
    hasPollingFiredRef.current = false
  }, [jobId])

  return { progress, status, refetch: query.refetch }
}
